import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, CheckCircle2, FilterX, MapPin } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { DataTable } from "../components/common/DataTable";
import { TimelineButton } from "../components/common/TimelineDialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { ClearableInput } from "../components/common/ClearableInput";
import { useDebouncedValue } from "../lib/useDebouncedValue";

const STATUS_STYLE = {
  GRN_PENDING: "bg-blue-100 text-blue-700",
  GRN_COMPLETED: "bg-emerald-100 text-emerald-700",
  DEFECTIVE_UNDER_REVIEW: "bg-red-100 text-red-700",
  SENT_BACK_TO_CLIENT: "bg-orange-100 text-orange-700",
  ACCEPTED_BY_CLIENT: "bg-lime-100 text-lime-700",
  ASSIGNED_TO_FIELD_USER: "bg-indigo-100 text-indigo-700",
  ACKNOWLEDGED_BY_FIELD_USER: "bg-teal-100 text-teal-700",
  RETURN_REQUESTED: "bg-amber-100 text-amber-700",
  INSTALLED_BY_FIELD_USER: "bg-sky-100 text-sky-700",
  COMPLETED_DATA_ENTRY: "bg-violet-100 text-violet-700",
};

// Card accent colors (border + bg + text) for the summary cards, keyed by status.
const CARD_ACCENT = {
  GRN_PENDING: "border-blue-200 bg-blue-50 text-blue-700",
  GRN_COMPLETED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  DEFECTIVE_UNDER_REVIEW: "border-red-200 bg-red-50 text-red-700",
  SENT_BACK_TO_CLIENT: "border-orange-200 bg-orange-50 text-orange-700",
  ACCEPTED_BY_CLIENT: "border-lime-200 bg-lime-50 text-lime-700",
  ASSIGNED_TO_FIELD_USER: "border-indigo-200 bg-indigo-50 text-indigo-700",
  ACKNOWLEDGED_BY_FIELD_USER: "border-teal-200 bg-teal-50 text-teal-700",
  RETURN_REQUESTED: "border-amber-200 bg-amber-50 text-amber-700",
  INSTALLED_BY_FIELD_USER: "border-sky-200 bg-sky-50 text-sky-700",
  COMPLETED_DATA_ENTRY: "border-violet-200 bg-violet-50 text-violet-700",
};

// Ordered list of statuses shown as cards & in the status filter (full material pipeline).
const ORDERED_STATUSES = [
  "GRN_PENDING", "GRN_COMPLETED", "DEFECTIVE_UNDER_REVIEW", "SENT_BACK_TO_CLIENT", "ACCEPTED_BY_CLIENT",
  "ASSIGNED_TO_FIELD_USER", "ACKNOWLEDGED_BY_FIELD_USER", "RETURN_REQUESTED", "INSTALLED_BY_FIELD_USER", "COMPLETED_DATA_ENTRY",
];

const STATUS_LABEL = (s) => s.replace(/_/g, " ");

const FIELD_AGE_STATUSES = ["ASSIGNED_TO_FIELD_USER", "ACKNOWLEDGED_BY_FIELD_USER"];

const ageStyle = (d) => {
  if (d == null) return "text-slate-400";
  if (d > 15) return "text-red-600 font-semibold";
  if (d > 7) return "text-amber-600 font-medium";
  return "text-slate-700";
};

export default function MaterialRegister() {
  const { ctx, options } = useWorkspace();
  const { has } = useAuth();
  const { drill, clearDrill } = useWorkspace();
  const pid = ctx.project?.id;
  const canSuper = has("MATERIAL.SUPER_DELETE");
  const canSealComplete = has("MATERIAL.SEAL_COMPLETE");
  const [sealTarget, setSealTarget] = useState(null);
  const [sealForm, setSealForm] = useState({ member_id: "", consumer_no: "", remark: "" });
  const [memberOpts, setMemberOpts] = useState([]);
  const [sealBusy, setSealBusy] = useState(false);
  const [types, setTypes] = useState([]);
  const [stores, setStores] = useState([]);
  const [holders, setHolders] = useState([]);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [rk, setRk] = useState(0);
  const [busy, setBusy] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [serialFrom, setSerialFrom] = useState("");
  const [serialTo, setSerialTo] = useState("");
  const dSerialFrom = useDebouncedValue(serialFrom);
  const dSerialTo = useDebouncedValue(serialTo);

  // Serial range flows into the DataTable params via filterValues (serial_from/serial_to).
  useEffect(() => {
    setFilterValues((v) => {
      const next = { ...v };
      if (dSerialFrom) next.serial_from = dSerialFrom; else delete next.serial_from;
      if (dSerialTo) next.serial_to = dSerialTo; else delete next.serial_to;
      return next;
    });
  }, [dSerialFrom, dSerialTo]);
  const [drillLabel, setDrillLabel] = useState(null);
  const [counts, setCounts] = useState({});
  const [countsTotal, setCountsTotal] = useState(0);
  const [byType, setByType] = useState([]);
  const [clearNonce, setClearNonce] = useState(0);

  // Clear everything: status card, all header filters, drill banner AND the search box (remount).
  const clearAllFilters = () => { setFilterValues({}); setSerialFrom(""); setSerialTo(""); setDrillLabel(null); setClearNonce((n) => n + 1); };

  // Consume a drill-down payload from the Dashboard Ground Ageing widget.
  useEffect(() => {
    if (drill && drill.key === "material-register") {
      const next = {};
      if (drill.material_type_id) next.material_type_id = drill.material_type_id;
      if (drill.current_status) next.current_status = drill.current_status;
      if (drill.assigned_field_user_id) next.assigned_field_user_id = drill.assigned_field_user_id;
      if (drill.field_held) next.field_held = true;
      setFilterValues(next);
      setSerialFrom(""); setSerialTo("");
      setDrillLabel(drill.label || "Filtered from Dashboard");
      clearDrill();
    }
  }, [drill, clearDrill]);

  const activeStatus = filterValues.current_status && filterValues.current_status !== "__all__" ? filterValues.current_status : "";

  const doArchive = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/materials/${archiveTarget.id}/super-archive`, { reason: reason.trim() });
      toast.success(`Archived ${archiveTarget.material_number}`);
      setArchiveTarget(null); setReason(""); setRk((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
    finally { setBusy(false); }
  };

  const sealTypeIds = new Set(types.filter((t) => (t.type_name || "").toLowerCase() === "seal").map((t) => t.id));

  const openSealComplete = (r) => {
    setSealTarget(r);
    setSealForm({ member_id: "", consumer_no: "", remark: "" });
    if (!memberOpts.length) {
      api.get("/seal-completion/members").then((res) =>
        setMemberOpts((res.data.items || []).map((m) => ({ value: m.id, label: `${m.member_name}${m.member_code ? ` (${m.member_code})` : ""}` })))).catch(() => {});
    }
  };
  const doSealComplete = async () => {
    setSealBusy(true);
    try {
      await api.post(`/materials/${sealTarget.id}/seal-complete`, sealForm);
      toast.success(`Seal ${sealTarget.material_number} marked as completed`);
      setSealTarget(null); setRk((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Completion failed"); }
    finally { setSealBusy(false); }
  };

  useEffect(() => {
    api.get("/material-types", { params: { page_size: 500 } }).then((r) => setTypes(r.data.items || []));
  }, []);

  // Field users for the Holder filter.
  useEffect(() => {
    api.get("/users", { params: { page_size: 500 } }).then((r) => {
      setHolders((r.data.items || []).filter((u) => u.is_field_user));
    }).catch(() => setHolders([]));
  }, []);

  // Stores for the register filter (scoped to the active project when one is selected).
  useEffect(() => {
    api.get("/stores", { params: { page_size: 500 } }).then((r) => {
      const all = r.data.items || [];
      setStores(pid ? all.filter((s) => s.project_id === pid) : all);
    });
  }, [pid]);

  // Status counts respect every active filter EXCEPT the status card selection itself.
  const loadCounts = useCallback(() => {
    const params = { ...(pid ? { project_id: pid } : {}) };
    if (filterValues.material_type_id && filterValues.material_type_id !== "__all__") params.material_type_id = filterValues.material_type_id;
    if (filterValues.store_id && filterValues.store_id !== "__all__") params.store_id = filterValues.store_id;
    if (filterValues.assigned_field_user_id) params.assigned_field_user_id = filterValues.assigned_field_user_id;
    if (filterValues.field_held) params.field_held = true;
    api.get("/materials/status-counts", { params }).then((r) => {
      setCounts(r.data.counts || {});
      setCountsTotal(r.data.total || 0);
      setByType(r.data.by_type || []);
    }).catch(() => { setCounts({}); setCountsTotal(0); setByType([]); });
  }, [pid, filterValues.material_type_id, filterValues.store_id, filterValues.assigned_field_user_id, filterValues.field_held]);

  useEffect(() => { loadCounts(); }, [loadCounts, rk]);

  const pickStatus = (s) => setFilterValues((v) => ({ ...v, current_status: s ? s : "__all__" }));

  const projectOpts = (options?.projects || []).map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name }));
  const typeOpts = types.map((t) => ({ value: t.id, label: t.type_name }));
  const storeOpts = stores.map((s) => ({ value: s.id, label: s.store_code ? `${s.store_code} — ${s.store_name}` : s.store_name }));
  const statusOpts = ORDERED_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL(s) }));
  const holderOpts = holders.map((u) => ({ value: u.id, label: u.user_code ? `${u.full_name} (${u.user_code})` : u.full_name }));

  const columns = [
    { key: "material_number", label: "Serial No.", mono: true, render: (r) => <span className="font-mono text-xs font-medium text-slate-900">{r.material_number}</span> },
    { key: "project_name", label: "Project", headerFilter: { type: "select", key: "project_id", options: projectOpts } },
    { key: "type_name", label: "Type", headerFilter: { type: "select", key: "material_type_id", options: typeOpts }, render: (r) => <Badge variant="secondary">{r.type_name}</Badge> },
    { key: "store_name", label: "Store", headerFilter: { type: "select", key: "store_id", options: storeOpts } },
    { key: "subdivision", label: "Subdivision", hideMobile: true, render: (r) => <span className="text-xs text-slate-600">{r.subdivision || "—"}</span> },
    { key: "circle", label: "Circle", hideMobile: true, render: (r) => <span className="text-xs text-slate-600">{r.circle || "—"}</span> },
    { key: "division", label: "Division", hideMobile: true, render: (r) => <span className="text-xs text-slate-600">{r.division || "—"}</span> },
    { key: "current_status", label: "Status", headerFilter: { type: "select", key: "current_status", options: statusOpts }, render: (r) => <Badge className={`${STATUS_STYLE[r.current_status] || "bg-slate-100 text-slate-600"} whitespace-nowrap hover:opacity-100`}>{STATUS_LABEL(r.current_status)}</Badge> },
    { key: "holder_name", label: "Holder", headerFilter: { type: "select", key: "assigned_field_user_id", options: holderOpts }, render: (r) => r.holder_name
      ? <span className="text-xs text-slate-700" data-testid={`holder-${r.id}`}>{r.holder_name}{r.holder_code && <span className="ml-1 font-mono text-slate-400">({r.holder_code})</span>}</span>
      : <span className="text-slate-300">—</span> },
    { key: "latest_geo", label: "Location", render: (r) => (
      r.latest_geo && r.latest_geo.lat != null ? (
        <a
          data-testid={`row-geo-${r.id}`}
          href={`https://maps.google.com/?q=${r.latest_geo.lat},${r.latest_geo.lng}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          title={`Latest location${r.latest_geo.action ? ` · ${STATUS_LABEL(r.latest_geo.action)}` : ""}${r.latest_geo.accuracy ? ` (±${Math.round(r.latest_geo.accuracy)}m)` : ""}`}
          className="inline-flex items-center text-emerald-600 hover:text-emerald-700"
        >
          <MapPin className="h-4 w-4" />
        </a>
      ) : <span className="text-slate-300" data-testid={`row-geo-none-${r.id}`}>—</span>
    ) },
    { key: "field_age_days", label: "Field Age (Days)", render: (r) => (r.field_age_days == null ? <span className="text-slate-400">—</span> : <span className={ageStyle(r.field_age_days)} data-testid={`field-age-${r.id}`}>{r.field_age_days}</span>) },
    { key: "created_at", label: "Uploaded", hideMobile: true, render: (r) => <span className="text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</span> },
  ];

  // Cards cover the full ordered pipeline plus any unexpected status present in the data.
  const extraStatuses = Object.keys(counts).filter((s) => !ORDERED_STATUSES.includes(s));
  const cardStatuses = [...ORDERED_STATUSES, ...extraStatuses];

  return (
    <div>
      <PageHeader title="Material Register" subtitle="All uploaded materials — server-side search, filter and pagination" />
      <ProjectScopeBanner />

      <Tabs defaultValue="register" className="w-full">
        <TabsList>
          <TabsTrigger value="register" data-testid="mr-tab-register">Register</TabsTrigger>
          <TabsTrigger value="ageing" data-testid="mr-tab-ageing">Status Ageing</TabsTrigger>
        </TabsList>
        <TabsContent value="register" className="mt-4">
      {drillLabel && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800" data-testid="mr-drill-banner">
          <span className="font-semibold">Filtered from Dashboard:</span>
          <Badge variant="outline" className="border-indigo-300">{drillLabel}</Badge>
          <button className="ml-auto text-indigo-700 underline hover:text-indigo-900" data-testid="mr-drill-clear" onClick={clearAllFilters}>Clear</button>
        </div>
      )}

      {/* Status summary cards */}
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">By Status</div>
      <div data-testid="status-cards" className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
        <button
          type="button"
          data-testid="status-card-all"
          onClick={() => pickStatus("")}
          className={`rounded-lg border px-3 py-2.5 text-left transition-all hover:shadow-sm ${!activeStatus ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 bg-white text-slate-700"}`}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">All</div>
          <div className="mt-0.5 text-xl font-bold tabular-nums">{countsTotal}</div>
        </button>
        {cardStatuses.map((s) => {
          const selected = activeStatus === s;
          return (
            <button
              key={s}
              type="button"
              data-testid={`status-card-${s}`}
              onClick={() => pickStatus(selected ? "" : s)}
              className={`rounded-lg border px-3 py-2.5 text-left transition-all hover:shadow-sm ${CARD_ACCENT[s] || "border-slate-200 bg-white text-slate-700"} ${selected ? "ring-2 ring-offset-1 ring-slate-800" : ""}`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wide leading-tight">{STATUS_LABEL(s)}</div>
              <div className="mt-0.5 text-xl font-bold tabular-nums">{counts[s] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* Type-wise counts */}
      {byType.length > 0 && (
        <div className="mb-6">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">By Material Type</div>
          <div data-testid="type-cards" className="flex flex-wrap gap-2">
            {byType.map((t) => {
              const active = filterValues.material_type_id === t.material_type_id;
              return (
                <button
                  key={t.material_type_id || "none"}
                  type="button"
                  data-testid={`type-card-${t.material_type_id || "none"}`}
                  onClick={() => setFilterValues((v) => ({ ...v, material_type_id: active ? "__all__" : t.material_type_id }))}
                  className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-all hover:shadow-sm ${active ? "border-indigo-500 bg-indigo-600 text-white ring-2 ring-indigo-200" : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"}`}
                >
                  <span className="font-medium">{t.type_name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>{t.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <DataTable
        key={`mr-${clearNonce}`}
        testid="materials-table"
        columns={columns}
        refreshKey={`${pid || "all"}-${rk}`}
        searchPlaceholder="Search by serial, Document No. or SIV No..."
        filterValues={filterValues}
        onFilterValuesChange={setFilterValues}
        filters={[]}
        toolbar={
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs text-slate-500">Serial From</Label><ClearableInput testid="mr-serial-from" value={serialFrom} onChange={setSerialFrom} placeholder="e.g. 0001" className="mt-1 w-32" /></div>
            <div><Label className="text-xs text-slate-500">Serial To</Label><ClearableInput testid="mr-serial-to" value={serialTo} onChange={setSerialTo} placeholder="e.g. 9999" className="mt-1 w-32" /></div>
            <Button variant="outline" size="sm" data-testid="mr-clear-filters" onClick={clearAllFilters}><FilterX className="mr-1.5 h-4 w-4" /> Clear Filters</Button>
          </div>
        }
        load={(params) => api.get("/materials", { params: { ...params, ...(pid ? { project_id: pid } : {}) } }).then((r) => r.data)}
        rowActions={(r) => (
          <div className="flex items-center gap-1">
            <TimelineButton materialId={r.id} serial={r.material_number} />
            {canSealComplete && sealTypeIds.has(r.material_type_id) && r.current_status !== "COMPLETED_DATA_ENTRY" && (
              <Button data-testid={`mat-seal-complete-${r.id}`} variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700" title="Mark seal completed (manual)" onClick={(e) => { e.stopPropagation(); openSealComplete(r); }}><CheckCircle2 className="h-4 w-4" /></Button>
            )}
            {canSuper && <Button data-testid={`mat-archive-${r.id}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-700" title="Superadmin archive" onClick={() => { setArchiveTarget(r); setReason(""); }}><Archive className="h-4 w-4" /></Button>}
          </div>
        )}
      />

      <Dialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <DialogContent data-testid="mat-archive-dialog">
          <DialogHeader><DialogTitle>Archive material (Superadmin)</DialogTitle></DialogHeader>
          {archiveTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                <span className="font-mono font-semibold">{archiveTarget.material_number}</span> — status <b>{archiveTarget.current_status}</b>. This has downstream history/references. Archiving preserves history but removes it from active operational use.
              </div>
              <Input data-testid="mat-archive-reason" placeholder="Mandatory reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)}>Cancel</Button>
            <Button data-testid="mat-archive-confirm" className="bg-red-600 hover:bg-red-700" disabled={busy || !reason.trim()} onClick={doArchive}>Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sealTarget} onOpenChange={(o) => !o && setSealTarget(null)}>
        <DialogContent data-testid="mat-seal-complete-dialog">
          <DialogHeader><DialogTitle>Mark Seal as Completed</DialogTitle></DialogHeader>
          {sealTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-emerald-800">
                Seal <span className="font-mono font-semibold">{sealTarget.material_number}</span> — current status <b>{STATUS_LABEL(sealTarget.current_status)}</b>. It will be marked Completed without a data entry.
              </div>
              <div>
                <Label className="text-xs">Field User / Member</Label>
                <div className="mt-1"><SearchableSelect testid="mat-seal-member" placeholder="Search member library..." value={sealForm.member_id} options={memberOpts} onChange={(v) => setSealForm((f) => ({ ...f, member_id: v }))} /></div>
              </div>
              <div>
                <Label className="text-xs">Consumer Number <span className="text-slate-400">(free text)</span></Label>
                <Input data-testid="mat-seal-consumer" className="mt-1" value={sealForm.consumer_no} onChange={(e) => setSealForm((f) => ({ ...f, consumer_no: e.target.value }))} placeholder="Enter consumer number" />
              </div>
              <div>
                <Label className="text-xs">Remark</Label>
                <Input data-testid="mat-seal-remark" className="mt-1" value={sealForm.remark} onChange={(e) => setSealForm((f) => ({ ...f, remark: e.target.value }))} placeholder="Optional remark" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSealTarget(null)}>Cancel</Button>
            <Button data-testid="mat-seal-complete-save" className="bg-emerald-600 hover:bg-emerald-700" disabled={sealBusy} onClick={doSealComplete}>{sealBusy ? "Saving…" : "Save & Complete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="ageing" className="mt-4">
          <StatusAgeingTab pid={pid} types={types} stores={stores} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusAgeingTab({ pid, types, stores }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fType, setFType] = useState("");
  const [fStore, setFStore] = useState("");
  const [fStatus, setFStatus] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get("/materials/status-ageing", { params: { ...(pid ? { project_id: pid } : {}), ...(fType ? { material_type_id: fType } : {}), ...(fStore ? { store_id: fStore } : {}), ...(fStatus ? { current_status: fStatus } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [pid, fType, fStore, fStatus]);
  const rows = data?.rows || [];
  const buckets = data?.buckets || [];
  const totals = buckets.map((b) => rows.reduce((s, r) => s + (r[b.key] || 0), 0));
  const grand = rows.reduce((s, r) => s + r.total, 0);

  return (
    <div data-testid="mr-ageing-tab">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-52"><span className="text-xs text-slate-500">Store</span>
          <SearchableSelect testid="mr-ageing-filter-store" value={fStore || "ALL"} onChange={(v) => setFStore(v === "ALL" ? "" : v)}
            options={[{ value: "ALL", label: "All Stores" }, ...stores.map((s) => ({ value: s.id, label: s.store_code ? `${s.store_code} — ${s.store_name}` : s.store_name }))]}
            placeholder="All Stores" className="mt-1" /></div>
        <div className="w-52"><span className="text-xs text-slate-500">Material Type</span>
          <SearchableSelect testid="mr-ageing-filter-type" value={fType || "ALL"} onChange={(v) => setFType(v === "ALL" ? "" : v)}
            options={[{ value: "ALL", label: "All Types" }, ...types.map((t) => ({ value: t.id, label: t.type_name }))]}
            placeholder="All Types" className="mt-1" /></div>
        <div className="w-56"><span className="text-xs text-slate-500">Material Status</span>
          <SearchableSelect testid="mr-ageing-filter-status" value={fStatus || "ALL"} onChange={(v) => setFStatus(v === "ALL" ? "" : v)}
            options={[{ value: "ALL", label: "All Statuses" }, ...ORDERED_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL(s) }))]}
            placeholder="All Statuses" className="mt-1" /></div>
      </div>
      <p className="mb-3 text-xs text-slate-500">Age = days since the material entered its current status. Counts only.</p>
      {loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-12 text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No materials found</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="mr-ageing-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/80 text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2.5 font-semibold">Material Type</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                {buckets.map((b) => <th key={b.key} className="px-3 py-2.5 text-right font-semibold">{b.label}</th>)}
                <th className="px-3 py-2.5 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.material_type_id}-${r.current_status}`} data-testid={`mr-ageing-row-${r.type_name}-${r.current_status}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><Badge variant="secondary">{r.type_name}</Badge></td>
                  <td className="px-3 py-2"><Badge className={`${STATUS_STYLE[r.current_status] || "bg-slate-100 text-slate-600"} whitespace-nowrap hover:opacity-100`}>{STATUS_LABEL(r.current_status)}</Badge></td>
                  {buckets.map((b) => (
                    <td key={b.key} className={`px-3 py-2 text-right tabular-nums ${r[b.key] > 0 && b.key === "b90p" ? "font-semibold text-red-600" : r[b.key] > 0 && b.key === "b31_90" ? "font-medium text-amber-600" : "text-slate-700"}`}>{r[b.key]}</td>
                  ))}
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.total}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold" data-testid="mr-ageing-total">
                <td className="px-3 py-2" colSpan={2}>Total</td>
                {buckets.map((b, i) => <td key={b.key} className="px-3 py-2 text-right tabular-nums">{totals[i]}</td>)}
                <td className="px-3 py-2 text-right tabular-nums">{grand}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
